import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Award, Calendar, Check, PlayCircle, Star, Users } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import PaymentProviderDialog from '../components/PaymentProviderDialog';

const normalizePlanKey = (str) =>
  (str || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

const PLAN_ID_MAP = {
  global_monthly: 'full_pass',
  cutting_monthly: 'weekly_haircuts',
  color_monthly: 'weekly_colormetrics',
  styling_monthly: 'video_library_pass',
  '9621d566-f412-4786-a5d3-f34db2b76f3d': 'live_streaming_seminars',
};

const FALLBACK_EYEBROWS = {
  weekly_haircuts: 'LIVE WEEKLY TRAINING',
  full_pass: 'COMPLETE ACCESS',
  weekly_colormetrics: 'COLOR EXPERT',
  live_streaming_seminars: 'LIVE EDUCATION ACCESS',
};

const PricingPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [contentPage, setContentPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const [plansRes, contentRes] = await Promise.all([
          axios.get(`${API}/plans`),
          axios.get(`${API}/content/pages/pricing?locale=${i18n.language}`).catch(() => ({ data: null }))
        ]);
        setPlans(plansRes.data);
        setContentPage(contentRes.data);
      } catch (e) {
        console.error('Failed to fetch plans', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPlans();
  }, [i18n.language]);

  const handleSubscribe = (planId) => {
    if (!user) {
      navigate('/login');
      return;
    }
    setSelectedPlanId(planId);
    setDialogOpen(true);
  };

  const sections = contentPage?.sections || {};
  const planCopy = sections.plan_copy || {};
  const locale = i18n.language?.startsWith('en') ? 'en' : 'el';

  const heroTitle = sections.hero?.title || (locale === 'en' ? 'Seminar Plans' : 'Πλάνα Σεμιναρίων');
  const heroSubtitle = sections.hero?.subtitle || (
    locale === 'en'
      ? 'Choose the education plan that fits your pace, goals, and growth.'
      : 'Επίλεξε το πλάνο εκπαίδευσης που ταιριάζει στον ρυθμό, τους στόχους και την εξέλιξή σου.'
  );

  const benefitItems = locale === 'en'
    ? [
        { icon: PlayCircle, label: 'On demand access', detail: '24/7 from every device' },
        { icon: Calendar, label: 'New content', detail: 'every week' },
        { icon: Award, label: 'Education by', detail: 'the MISEL GROUP team' },
        { icon: Users, label: 'Join a community', detail: 'that keeps evolving' },
      ]
    : [
        { icon: PlayCircle, label: 'On demand πρόσβαση', detail: '24/7 από κάθε συσκευή' },
        { icon: Calendar, label: 'Νέο περιεχόμενο', detail: 'κάθε εβδομάδα' },
        { icon: Award, label: 'Εκπαίδευση από', detail: 'την ομάδα MISEL GROUP' },
        { icon: Users, label: 'Γίνε μέλος μιας κοινότητας', detail: 'που εξελίσσεται' },
      ];

  const getPlanCopyKey = (plan) => {
    if (plan.slug && planCopy[plan.slug] !== undefined) return plan.slug;
    const mapped = PLAN_ID_MAP[plan.id];
    if (mapped && planCopy[mapped] !== undefined) return mapped;
    if (plan.id && planCopy[plan.id] !== undefined) return plan.id;
    const nameKey = normalizePlanKey(plan.name || plan.title || '');
    if (nameKey && planCopy[nameKey] !== undefined) return nameKey;
    return mapped || normalizePlanKey(plan.name || plan.id || '');
  };

  const getFallbackEyebrow = (plan, copyKey) => {
    const normalizedName = normalizePlanKey(plan.name || '');
    if (FALLBACK_EYEBROWS[copyKey]) return FALLBACK_EYEBROWS[copyKey];
    if (normalizedName.includes('weekly_haircuts') || normalizedName.includes('haircuts')) {
      return FALLBACK_EYEBROWS.weekly_haircuts;
    }
    if (normalizedName.includes('full_pass') || plan.scope === 'global') {
      return FALLBACK_EYEBROWS.full_pass;
    }
    if (normalizedName.includes('colormetrics') || normalizedName.includes('color')) {
      return FALLBACK_EYEBROWS.weekly_colormetrics;
    }
    if (normalizedName.includes('live_streaming') || normalizedName.includes('seminars')) {
      return FALLBACK_EYEBROWS.live_streaming_seminars;
    }
    return 'MISEL EDUCATION';
  };

  const getPlanCardCopy = (plan, legacyCopy, copyKey) => {
    const included = plan[`sales_included_${locale}`]?.length
      ? plan[`sales_included_${locale}`]
      : legacyCopy?.included || [];

    return {
      eyebrow: plan[`sales_eyebrow_${locale}`] || legacyCopy?.eyebrow || getFallbackEyebrow(plan, copyKey),
      title: plan[`sales_title_${locale}`] || legacyCopy?.title || plan.name,
      subtitle: plan[`sales_subtitle_${locale}`] || legacyCopy?.subtitle || '',
      description: plan[`sales_description_${locale}`] || plan[`description_${locale}`] || legacyCopy?.description || '',
      included,
      note: legacyCopy?.note,
      cta_label: legacyCopy?.cta_label,
      image_url: plan.image_url || legacyCopy?.image_url || '',
      image_alt: plan[`image_alt_${locale}`] || plan.name || '',
    };
  };

  const isFeaturedPlan = (plan) => {
    const normalizedName = (plan.name || '').toUpperCase();
    return normalizedName.includes('FULL PASS') || plan.scope === 'global';
  };

  const getVisibleBullets = (items) => (Array.isArray(items) ? items.filter(Boolean).slice(0, 5) : []);

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      <div className="relative overflow-x-hidden px-4 pb-20 pt-24 sm:px-8 md:px-12">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute left-[-12rem] top-16 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-[-10rem] top-28 h-[32rem] w-[32rem] rounded-full bg-red-950/20 blur-3xl" />
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative mx-auto mb-14 max-w-4xl text-center"
        >
          <p className="mb-4 text-xs font-semibold uppercase tracking-[0.32em] text-primary/80">
            MISEL GROUP DIGITAL LAB
          </p>
          <h1 className="font-roboto text-4xl font-bold leading-tight text-white sm:text-5xl lg:text-6xl">
            {heroTitle}
          </h1>
          <div className="mx-auto my-6 flex w-44 items-center justify-center gap-3">
            <span className="h-px flex-1 bg-gradient-to-r from-transparent to-primary/70" />
            <span className="h-1.5 w-1.5 rotate-45 bg-primary" />
            <span className="h-px flex-1 bg-gradient-to-l from-transparent to-primary/70" />
          </div>
          <p className="mx-auto max-w-2xl text-base leading-8 text-white/65 sm:text-lg">
            {heroSubtitle}
          </p>
        </motion.div>

        {(sections.plans_intro?.title || sections.plans_intro?.body) && (
          <div className="relative mx-auto mb-10 max-w-3xl text-center">
            {sections.plans_intro?.title && (
              <h2 className="mb-3 font-roboto text-2xl font-bold text-white">{sections.plans_intro.title}</h2>
            )}
            {sections.plans_intro?.body && (
              <p className="whitespace-pre-line text-sm leading-7 text-white/55">{sections.plans_intro.body}</p>
            )}
          </div>
        )}

        {loading ? (
          <div className="relative mx-auto grid max-w-[92rem] grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-[42rem] animate-pulse rounded-sm border border-white/10 bg-white/[0.04]" />
            ))}
          </div>
        ) : (
          <div className="relative mx-auto grid max-w-[92rem] grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
            {plans.map((plan, index) => {
              const copyKey = getPlanCopyKey(plan);
              const legacyCopy = planCopy[copyKey] && typeof planCopy[copyKey] === 'object'
                ? planCopy[copyKey]
                : null;
              const copy = getPlanCardCopy(plan, legacyCopy, copyKey);
              const featured = isFeaturedPlan(plan);
              const visibleBullets = getVisibleBullets(copy.included);

              return (
                <motion.div
                  key={plan.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`group relative flex min-h-[42rem] flex-col overflow-hidden rounded-sm border bg-[#0b0b0c]/95 shadow-2xl shadow-black/40 transition duration-300 ${
                    featured
                      ? 'border-primary/80 ring-1 ring-primary/35 shadow-primary/10'
                      : 'border-white/10 hover:border-primary/35'
                  }`}
                >
                  {featured && (
                    <div className="absolute left-1/2 top-0 z-20 flex -translate-x-1/2 items-center gap-1.5 bg-primary px-4 py-1.5 text-[0.66rem] font-bold uppercase tracking-[0.22em] text-white shadow-lg shadow-primary/30">
                      <Star size={12} fill="white" />
                      {locale === 'en' ? 'MOST POPULAR' : 'ΠΙΟ ΔΗΜΟΦΙΛΕΣ'}
                    </div>
                  )}

                  {copy.image_url && (
                    <div className="relative h-44 overflow-hidden border-b border-white/10 bg-black">
                      <img
                        src={copy.image_url}
                        alt={copy.image_alt}
                        className="h-full w-full object-cover opacity-80 saturate-[0.85] transition duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0b0b0c] via-transparent to-black/20" />
                    </div>
                  )}

                  <div className="flex flex-1 flex-col p-6 sm:p-7">
                    <div className="mb-5">
                      <p className="mb-3 text-[0.68rem] font-bold uppercase tracking-[0.24em] text-primary/90">
                        {copy.eyebrow}
                      </p>
                      <h3 className="mb-3 min-h-[3.7rem] font-roboto text-2xl font-bold leading-tight text-white">
                        {copy.title || plan.name}
                      </h3>
                      {copy.subtitle && (
                        <p className="min-h-[3rem] text-sm font-semibold leading-6 text-white/78">
                          {copy.subtitle}
                        </p>
                      )}
                      {copy.description && (
                        <p className="mt-4 max-h-28 overflow-hidden text-sm leading-7 text-white/50">
                          {copy.description}
                        </p>
                      )}
                    </div>

                    <div className="mb-5 h-px bg-gradient-to-r from-primary/50 via-white/10 to-transparent" />

                    {visibleBullets.length > 0 ? (
                      <ul className="mb-6 space-y-3">
                        {visibleBullets.map((item, i) => (
                          <li key={i} className="flex items-start gap-3 text-sm leading-6 text-white/72">
                            <Check size={16} className="mt-1 flex-shrink-0 text-primary" />
                            <span className="min-w-0">{item}</span>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="mb-6 flex-1" />
                    )}

                    <div className="mt-auto pt-2">
                      <div className="mb-5 border-t border-white/10 pt-5">
                        <span className="font-roboto text-4xl font-bold text-white">€{plan.price}</span>
                        <span className="ml-2 text-sm text-white/45">/{t('pricing.month')}</span>
                      </div>

                      {copy.note && (
                        <p className="mb-4 max-h-10 overflow-hidden text-xs leading-5 text-white/40">{copy.note}</p>
                      )}

                      <Button
                        onClick={() => handleSubscribe(plan.id)}
                        data-testid={`subscribe-${plan.id}`}
                        className={`h-12 w-full rounded-sm font-bold uppercase tracking-[0.14em] transition ${
                          featured
                            ? 'bg-primary text-white shadow-lg shadow-primary/25 hover:bg-primary/90'
                            : 'border border-primary/60 bg-transparent text-white hover:bg-primary hover:text-white'
                        }`}
                      >
                        {copy.cta_label || t('pricing.subscribe')}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="relative mx-auto mt-14 max-w-[92rem]"
        >
          <div className="grid grid-cols-1 border border-white/10 bg-white/[0.035] md:grid-cols-2 xl:grid-cols-4">
            {benefitItems.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="flex items-center gap-4 border-b border-white/10 p-5 last:border-b-0 md:[&:nth-child(3)]:border-b-0 md:[&:nth-child(4)]:border-b-0 xl:border-b-0 xl:border-r xl:border-white/10 xl:last:border-r-0">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary">
                    <Icon size={20} />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{item.label}</p>
                    <p className="text-sm text-white/50">{item.detail}</p>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-white/55">
              {locale === 'en' ? 'Questions? We are here for you.' : 'Έχεις απορίες; Είμαστε εδώ για σένα.'}
            </p>
            <span className="mt-2 inline-block text-sm font-semibold text-primary">
              {locale === 'en' ? 'Contact us →' : 'Επικοινώνησε μαζί μας →'}
            </span>
          </div>
        </motion.div>
      </div>

      <PaymentProviderDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode="subscription"
        planId={selectedPlanId}
        basePrice={
          selectedPlanId && plans.find((p) => p.id === selectedPlanId)
            ? plans.find((p) => p.id === selectedPlanId).price
            : 0
        }
        amountLabel={
          selectedPlanId && plans.find((p) => p.id === selectedPlanId)
            ? `€${plans.find((p) => p.id === selectedPlanId).price}/${t('pricing.month')}`
            : undefined
        }
      />
    </div>
  );
};

export default PricingPage;
